import { useMutation } from '@apollo/client/react';
import { get } from 'lodash-es';

import useAccount from '@zen/utils/hooks/useAccount';
import { useNotification } from '@zen/utils/hooks/useNotification';
import { performMutation } from '@zen/utils/performMutation';

import {
  UpdateAnalyticsAccountSettingsDocument,
  type UpdateAnalyticsAccountSettingsMutationResult,
  type UpdateAnalyticsAccountSettingsMutationVariables
} from '../graphql/updateAnalyticsAccountSettings.generated';

export const useUpdateAccountAnalyticsSettings = () => {
  const { addError, addSuccess } = useNotification();
  const { userProfile } = useAccount();
  const { uuid: userId } = userProfile;
  const [updateAccountAnalyticsSettings, { loading }] = useMutation<
    UpdateAnalyticsAccountSettingsMutationResult,
    UpdateAnalyticsAccountSettingsMutationVariables
  >(UpdateAnalyticsAccountSettingsDocument);

  const handleUpdateAccountAnalyticsSettings = async (
    accountId: string,
    analyticsSettings: { analyticsEnabled: boolean; analyticsSharedUserEnabled: boolean }
  ) => {
    const { analyticsEnabled, analyticsSharedUserEnabled } = analyticsSettings;
    const analyticsUserId: string = analyticsSharedUserEnabled ? accountId : userId;

    return await performMutation({
      mutationFn: () =>
        updateAccountAnalyticsSettings({
          awaitRefetchQueries: true,
          refetchQueries: ['analyticsAccountSettings'],
          variables: {
            input: {
              accountId,
              analyticsEnabled,
              analyticsSharedUserEnabled,
              analyticsUserId
            }
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Analytics setting could not be updated. Please try again.'));
      },
      onSuccess: () => {
        addSuccess(`Analytics setting ${analyticsEnabled ? 'enabled' : 'disabled'}.`);
      }
    });
  };

  return { handleUpdateAccountAnalyticsSettings, loading };
};
